// src/pages/HomePage.js
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import Avatar from "../components/Avatar";

function formatDate(date) {
  return new Date(date).toLocaleDateString("sv-SE", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

const dayNames = ["sön", "mån", "tis", "ons", "tors", "fre", "lör"];

function getCoveredDays(startDate, endDate, weekStart, weekEnd) {
  const s = new Date(startDate);
  const e = new Date(endDate);
  const clipStart = s < weekStart ? new Date(weekStart) : new Date(s);
  const clipEnd = e > weekEnd ? new Date(weekEnd) : new Date(e);

  const covered = [];
  for (let d = new Date(clipStart); d <= clipEnd; d.setDate(d.getDate() + 1)) {
    covered.push(d.getDay());
  }
  const uniq = [...new Set(covered)];
  return uniq.map((n) => dayNames[n]).join(", ");
}

function getWeekRange(offset = 0) {
  const today = new Date();
  const dayOfWeek = (today.getDay() + 6) % 7; // 0 = måndag
  const monday = new Date(today);
  monday.setDate(today.getDate() - dayOfWeek + offset * 7);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return { monday, sunday };
}

export default function HomePage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [jour, setJour] = useState({ thisWeek: [], nextWeek: [] });
  const [vacations, setVacations] = useState([]);
  const [stats, setStats] = useState({ active: 0, doneWeek: 0, users: 0 });
  const [bugs, setBugs] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        // Aktiva jobb (bara mina)
        const jobRes = await api.get("/todos");
        const myJobs = jobRes.data.todos
          .filter((t) => !t.completed && t.assignedTo?.id === user?.id)
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
          .slice(0, 5);
        setJobs(myJobs);

        // Jour denna & nästa vecka
        const jourRes = await api.get("/jour");
        const allJours = jourRes.data.jours || [];

        const { monday: thisMon, sunday: thisSun } = getWeekRange(0);
        const thisWeekJours = allJours.filter((j) => {
          const s = new Date(j.startDate);
          const e = new Date(j.endDate);
          return s <= thisSun && e >= thisMon;
        });

        const { monday: nextMon, sunday: nextSun } = getWeekRange(1);
        const nextWeekJours = allJours.filter((j) => {
          const s = new Date(j.startDate);
          const e = new Date(j.endDate);
          return s <= nextSun && e >= nextMon;
        });

        setJour({ thisWeek: thisWeekJours, nextWeek: nextWeekJours });

        // ---- I useEffect ----
        const vacRes = await api.get("/vacations");
        const allVac = vacRes.data.vacations || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const ongoing = allVac.filter(
          v => v.status === "Beviljad" &&
              new Date(v.startDate) <= today &&
              new Date(v.endDate) >= today
        );

        const upcoming = allVac.filter(
          v => v.status === "Beviljad" &&
              new Date(v.startDate) > today
        );

        setVacations({ ongoing, upcoming });


        // Statistik (admin)
        if (user?.role === "admin") {
          const statsRes = await api.get("/admin/stats");
          setStats(statsRes.data);
        }

        // Buggar (dev)
        if (user?.isDev) {
          const bugRes = await api.get("/bugs/latest?limit=3");
          setBugs(bugRes.data);
        }
      } catch (e) {
        console.error("Dashboard load error", e);
      }
    };
    load();
  }, [user]);

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <Avatar
          initials={user?.avatar?.initials}
          color={user?.avatar?.color}
          size={48}
        />
        <div className="ms-3">
          <h4 className="mb-0">
            Hej, {user?.firstName} {user?.lastName}
          </h4>
          <small className="text-muted">
            {formatDate(new Date())} (v.{getWeekNumber(new Date())})
          </small>
        </div>
      </div>

      <div className="row g-4">
        {/* Mina jobb */}
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Mina tilldelade jobb</h5>
              {jobs.length === 0 ? (
                <p className="text-muted">Inga aktiva jobb just nu.</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {jobs.map((j) => (
                    <li key={j.id} className="list-group-item">
                      {j.text}{" "}
                      {j.dueDate && (
                        <span className="badge bg-light text-dark ms-2">
                          {new Date(j.dueDate).toLocaleDateString("sv-SE")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <a href="/todo" className="btn btn-sm btn-outline-primary mt-3">
                Visa alla jobb
              </a>
            </div>
          </div>
        </div>

        {/* Jour */}
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Jour</h5>

              {/* Denna vecka */}
              <div className="card mb-3">
                <div className="card-header fw-bold">Jour denna vecka</div>
                <div className="card-body">
                  {jour.thisWeek.length ? (
                    jour.thisWeek.map((j) => {
                      const { monday: thisMon, sunday: thisSun } = getWeekRange(0);
                      return (
                        <span
                          key={j.id}
                          className="badge bg-light text-dark me-2 p-2 d-inline-flex align-items-center"
                        >
                          <Avatar
                            initials={j.user?.avatar?.initials}
                            color={j.user?.avatar?.color}
                            size={18}
                          />
                          <span className="ms-1">
                            {j.user?.firstName} {j.user?.lastName}:{" "}
                            {getCoveredDays(j.startDate, j.endDate, thisMon, thisSun)}
                          </span>
                        </span>
                      );
                    })
                  ) : (
                    <div className="text-muted">Ingen jour denna vecka</div>
                  )}
                </div>
              </div>

              {/* Nästa vecka */}
              <div className="card mb-3">
                <div className="card-header fw-bold">Jour nästa vecka</div>
                <div className="card-body">
                  {jour.nextWeek.length ? (
                    jour.nextWeek.map((j) => {
                      const { monday: nextMon, sunday: nextSun } = getWeekRange(1);
                      return (
                        <span
                          key={j.id}
                          className="badge bg-light text-dark me-2 p-2 d-inline-flex align-items-center"
                        >
                          <Avatar
                            initials={j.user?.avatar?.initials}
                            color={j.user?.avatar?.color}
                            size={18}
                          />
                          <span className="ms-1">
                            {j.user?.firstName} {j.user?.lastName}:{" "}
                            {getCoveredDays(j.startDate, j.endDate, nextMon, nextSun)}
                          </span>
                        </span>
                      );
                    })
                  ) : (
                    <div className="text-muted">Ingen jour nästa vecka</div>
                  )}
                </div>
              </div>

              <a href="/jour" className="btn btn-sm btn-outline-primary">
                Visa jour
              </a>
            </div>
          </div>
        </div>

        {/* Semestrar */}
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Semestrar</h5>

              {/* Pågående */}
              <div className="card mb-3">
                <div className="card-header fw-bold">Pågående semestrar</div>
                <div className="card-body">
                  {vacations.ongoing?.length ? (
                    vacations.ongoing.map(v => (
                      <span
                        key={v._id}
                        className="badge bg-light text-dark me-2 p-2 d-inline-flex align-items-center"
                      >
                        <Avatar
                          initials={v.userId?.avatar?.initials}
                          color={v.userId?.avatar?.color}
                          size={18}
                        />
                        <span className="ms-1">
                          {v.userId?.firstName} {v.userId?.lastName}:{" "}
                          {new Date(v.startDate).toLocaleDateString("sv-SE")} –{" "}
                          {new Date(v.endDate).toLocaleDateString("sv-SE")}
                        </span>
                      </span>
                    ))
                  ) : (
                    <div className="text-muted">Inga pågående semestrar</div>
                  )}
                </div>
              </div>

              {/* Kommande */}
              <div className="card mb-3">
                <div className="card-header fw-bold">Kommande semestrar</div>
                <div className="card-body">
                  {vacations.upcoming?.length ? (
                    vacations.upcoming.map(v => (
                      <span
                        key={v._id}
                        className="badge bg-light text-dark me-2 p-2 d-inline-flex align-items-center"
                      >
                        <Avatar
                          initials={v.userId?.avatar?.initials}
                          color={v.userId?.avatar?.color}
                          size={18}
                        />
                        <span className="ms-1">
                          {v.userId?.firstName} {v.userId?.lastName}:{" "}
                          {new Date(v.startDate).toLocaleDateString("sv-SE")} –{" "}
                          {new Date(v.endDate).toLocaleDateString("sv-SE")}
                        </span>
                      </span>
                    ))
                  ) : (
                    <div className="text-muted">Inga kommande semestrar</div>
                  )}
                </div>
              </div>

              <a
                href="/vacation-overview"
                className="btn btn-sm btn-outline-primary"
              >
                Semesteröversikt
              </a>
            </div>
          </div>
        </div>


        {/* Statistik */}
        {user?.role === "admin" && (
          <div className="col-md-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title">Statistik</h5>
                <ul className="list-group list-group-flush">
                  <li className="list-group-item">Aktiva jobb: {stats.active}</li>
                  <li className="list-group-item">
                    Slutförda denna vecka: {stats.doneWeek}
                  </li>
                  <li className="list-group-item">
                    Antal användare: {stats.users - 1}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Buggar */}
        {user?.isDev && (
          <div className="col-12">
            <div className="card shadow-sm border-danger">
              <div className="card-body">
                <h5 className="card-title text-danger">Senaste buggrapporter</h5>
                {bugs.length === 0 ? (
                  <p className="text-muted">Inga buggrapporter ännu.</p>
                ) : (
                  <ul className="list-group list-group-flush">
                    {bugs.map((b) => (
                      <li key={b._id} className="list-group-item">
                        <strong>{b.category}</strong>: {b.description}
                      </li>
                    ))}
                  </ul>
                )}
                <a
                  href="/bug-list"
                  className="btn btn-sm btn-outline-danger mt-3"
                >
                  Se buggsystem
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Hjälp för veckonummer
function getWeekNumber(d) {
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}
